import { useCallback, useState } from 'react';
import { Accept, FileRejection, useDropzone } from 'react-dropzone';

interface AudioDropzoneProps {
  maxFiles?: number;
  accept?: Accept;
  onChange?: (files: File[]) => void;
  beforeAddFile?: (file: File) => Promise<boolean> | boolean;
}

export function useAudioDropzone({ maxFiles = 5, accept = { 'audio/*': [] }, onChange, beforeAddFile }: AudioDropzoneProps = {}) {
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);

  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (acceptedFiles.length + files.length > maxFiles) {
        setError(`Maximum ${maxFiles} audio files can be uploaded`);
        return;
      }

      if (beforeAddFile) {
        const validatedFiles = [];
        for (const file of acceptedFiles) {
          const isValid = await beforeAddFile(file);
          if (isValid) {
            validatedFiles.push(file);
          }
        }
        acceptedFiles = validatedFiles;
      }

      setFiles((prev) => {
        const next = [...prev, ...acceptedFiles].slice(0, maxFiles);
        onChange && onChange(next);
        return next;
      });
      setError(fileRejections.length > 0 ? 'Invalid file type' : null);
    },
    [files, maxFiles, onChange, beforeAddFile],
  );

  const removeFile = (file: File) => {
    setFiles((prev) => {
      const newFiles = prev.filter((f) => f !== file);
      onChange && onChange(newFiles);
      return newFiles;
    });
  };

  const clearFiles = () => {
    setFiles([]);
    onChange && onChange([]);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxFiles,
    multiple: true,
  });

  return {
    files,
    error,
    getRootProps,
    getInputProps,
    isDragActive,
    removeFile,
    clearFiles,
  };
}
